import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, Space, Stack, StackProps } from "@mantine/core";
import { ReviewDestination, ReviewDestinationInput } from "src/graphql/generated/graphql";
import { v4 as uuidv4 } from "uuid";

import { ReviewDestinationType } from "../../hooks/reviews";
import { sortArrayByOtherArray } from "../../utils/sortArray";
import { Link } from "../Link";
import { Item } from "./item";

export interface ReviewDestinationsEditorProps extends Omit<StackProps, "onChange"> {
    value: ReviewDestinationInput[];
    reviewDestinations: ReviewDestinationInput[];
    noReviewDestinationsLabel?: ReactNode;
    enableCustomProductReviewDestinations?: boolean;
    onChange: (value: ReviewDestinationInput[]) => void;
}

const compareReviewDestinationInput = (a: ReviewDestinationInput, b: ReviewDestinationInput) => {
    return a.type === b.type && a.key === b.key;
};

export const ReviewDestinationsEditor = ({
    value,
    reviewDestinations,
    noReviewDestinationsLabel,
    enableCustomProductReviewDestinations,
    onChange,
    ...rest
}: ReviewDestinationsEditorProps) => {
    const { t } = useTranslation(["common"]);

    const handleToggleReviewDestination = useCallback(
        (reviewDestination: ReviewDestinationInput) => {
            const newReviewDestinations = [...(value || [])];
            const index = (value || []).findIndex(
                (item) => item?.type === reviewDestination?.type && (!item?.key || item?.key === reviewDestination?.key)
            );

            if (index >= 0) {
                newReviewDestinations.splice(index, 1);
                onChange?.(newReviewDestinations);
                return;
            }

            newReviewDestinations.push(reviewDestination);
            onChange?.(sortArrayByOtherArray(newReviewDestinations, reviewDestinations, compareReviewDestinationInput));
        },
        [onChange, reviewDestinations, value]
    );

    const handleReviewDestinationNameChange = useCallback(
        ({ type, key }: ReviewDestination, name: string) => {
            const newValue = (value || []).map((reviewDestination) => {
                if (reviewDestination?.type === type && (!reviewDestination?.key || reviewDestination?.key === key)) {
                    return { ...reviewDestination, name };
                }

                return reviewDestination;
            });

            onChange?.(newValue);
        },
        [onChange, value]
    );

    const handleReviewDestinationConfigChange = useCallback(
        ({ type, key }: ReviewDestination, config: string) => {
            const newValue = (value || []).map((reviewDestination) => {
                if (reviewDestination?.type === type && (!reviewDestination?.key || reviewDestination?.key === key)) {
                    return { ...reviewDestination, config };
                }

                return reviewDestination;
            });

            onChange?.(newValue);
        },
        [onChange, value]
    );

    const handleAddCustomReviewDestination = useCallback(() => {
        onChange?.([
            ...(value || []),
            {
                type: ReviewDestinationType.custom,
                key: uuidv4().replace(/-/g, ""),
                name: "",
                config: JSON.stringify({
                    confirmSubmission: true,
                }),
            },
        ]);
    }, [onChange, value]);

    const { items, alreadyIncludedIds } = useMemo(() => {
        const items: {
            value: ReviewDestinationInput;
            reviewDestination: ReviewDestination;
            isSelected: boolean;
        }[] = [];

        const alreadyIncludedIds: string[] = [];

        for (const reviewDestination of reviewDestinations) {
            const itemValue = (value || []).find(
                (value) =>
                    value?.type === reviewDestination?.type &&
                    (!reviewDestination?.key || reviewDestination?.key === value?.key)
            );

            const isSelected = !!itemValue;
            if (isSelected) {
                alreadyIncludedIds.push([itemValue?.type, itemValue?.key].join("/"));
            }

            items.push({
                reviewDestination,
                isSelected,
                value: itemValue,
            });
        }

        for (const itemValue of value || []) {
            const itemId = [itemValue?.type, itemValue?.key].join("/");

            if (alreadyIncludedIds.indexOf(itemId) >= 0) {
                continue;
            }

            items.push({
                reviewDestination: {
                    type: itemValue?.type,
                    key: itemValue?.key,
                    name: itemValue?.name,
                },
                value: itemValue,
                isSelected: true,
            });
        }

        return { items, alreadyIncludedIds };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(value), JSON.stringify(reviewDestinations)]);

    useEffect(() => {
        const ttReviewDestination = alreadyIncludedIds.find((id) => id?.indexOf?.("tt") === 0);

        if (!ttReviewDestination) {
            const reviewDestination = reviewDestinations.find(
                (reviewDestination) => reviewDestination?.type === ReviewDestinationType.tt
            );

            if (reviewDestination) {
                new Promise((resolve) => setTimeout(resolve, 40)).then(() => {
                    handleToggleReviewDestination(reviewDestination);
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(alreadyIncludedIds), JSON.stringify(reviewDestinations)]);

    return (
        <Stack {...rest} spacing="0">
            {!reviewDestinations?.length && noReviewDestinationsLabel && noReviewDestinationsLabel}
            {items.map((item, index) => (
                <Item
                    {...item}
                    key={`${index}${item?.reviewDestination?.type || item?.value?.type}${
                        item?.reviewDestination?.key || item?.value?.key
                    }`}
                    onToggleReviewDestination={handleToggleReviewDestination}
                    onReviewDestinationNameChange={handleReviewDestinationNameChange}
                    onReviewDestinationConfigChange={handleReviewDestinationConfigChange}
                />
            ))}
            {enableCustomProductReviewDestinations && (
                <Flex mt="sm" align="center">
                    <Space w="20px" />
                    <Box ml="md">
                        <Link
                            onClick={(event) => {
                                event?.preventDefault?.();
                                event?.stopPropagation?.();

                                handleAddCustomReviewDestination();
                            }}
                        >
                            {t("campaign_productreviewdestinations_addcustom")}
                        </Link>
                    </Box>
                </Flex>
            )}
        </Stack>
    );
};
